"use client";

import * as React from "react";
import Link from "next/link";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { fetchSanityData } from "@/lib/sanity";
import { Search } from "./Search";
import { SearchIcon } from "@sanity/icons"; // Sanity's Search Icon

export function Header() {
  const [categories, setCategories] = React.useState<
    { title: string; slug: string }[]
  >([]);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  // Fetch categories dynamically from Sanity
  React.useEffect(() => {
    const fetchCategories = async () => {
      const query = `*[_type == "category"] { title, "slug": slug.current }`;
      const data = await fetchSanityData(query);
      setCategories(data || []);
    };

    fetchCategories();
  }, []);

  return (
    <header className="border-b bg-white shadow-md">
      <div className="container mx-auto flex items-center justify-between px-4 py-3">
        {/* Blog Title */}
        <Link href="/" className="text-2xl font-bold text-gray-800">
          General Insights
        </Link>

        {/* Desktop Search Bar */}
        <div className="hidden md:flex flex-1 justify-center items-center px-4">
          <Search />
        </div>

        {/* Mobile Search Icon */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setIsSearchOpen((prev) => !prev)}
            className="text-gray-600 p-2 hover:text-gray-800 focus:outline-none"
            aria-label="Toggle Search"
          >
            <SearchIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation Menu */}
        <NavigationMenu>
          <NavigationMenuList>
            {/* Blog Dropdown */}
            <NavigationMenuItem>
              <NavigationMenuTrigger>Blog</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid gap-3 p-4 md:w-[300px] max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                  <ListItem href="/blog" title="All Posts">
                    Browse all blog posts and explore our latest content.
                  </ListItem>
                  {categories.map((category) => (
                    <ListItem
                      key={category.slug}
                      href={`/categories/${category.slug}`}
                      title={category.title}
                    >
                      Explore posts under the {category.title} category.
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* About */}
            <NavigationMenuItem>
              <Link href="/about" passHref legacyBehavior>
                <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                  About
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {/* Mobile Search Bar */}
      {isSearchOpen && (
        <div className="px-4 py-3 md:hidden border-t bg-white">
          <Search />
        </div>
      )}
    </header>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={`block select-none space-y-1 rounded-md p-3 leading-none no-underline transition-colors hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-200 focus:text-gray-900 ${className}`}
          {...props}
        >
          <div className="text-sm font-medium">{title}</div>
          <p className="text-sm text-gray-600">{children}</p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";
